import React, { useRef } from 'react';
import { Paper, CircularProgress } from '@material-ui/core';
import { InventoryReportSnapshotItemRow } from './types';
import TableHeader, { SortConfig } from './TableHeader';
import VirtualTableBody from './VirtualTableBody';
import TablePagination from './TablePagination';
import { RowHandlers } from './MemoizedTableRow';
import { VirtualizerApi } from './VirtualTableBody';

const ROW_HEIGHT = 40;
/** Fixed max height so table fits in viewport - avoids page scrollbar */
const MAX_TABLE_HEIGHT = 'calc(100vh - 350px)';

export interface InventoryOrderVirtualTableProps {
    data: InventoryReportSnapshotItemRow[];
    loading?: boolean;
    page: number;
    pageSize: number;
    totalCount: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    sortConfig?: SortConfig | null;
    onSort?: (field: string) => void;
    editable: boolean;
    unsavedChanges: Record<string, { originalValue: number; newValue: number }>;
    handlers: RowHandlers;
    errorIconClassName?: string;
    focusRowId?: string | null;
    savingRowId?: string | null;
    emptyMessage?: string;
    virtualizerRef?: React.MutableRefObject<VirtualizerApi | null>;
}

const InventoryOrderVirtualTable: React.FC<InventoryOrderVirtualTableProps> = ({
    data,
    loading = false,
    page,
    pageSize,
    totalCount,
    onPageChange,
    onPageSizeChange,
    sortConfig,
    onSort,
    editable,
    unsavedChanges,
    handlers,
    errorIconClassName,
    focusRowId,
    savingRowId,
    emptyMessage = 'No data',
    virtualizerRef,
}) => {
    const parentRef = useRef<HTMLDivElement>(null);

    return (
        <Paper
            elevation={2}
            style={{
                width: '100%',
                maxWidth: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
            }}
        >
            <div
                ref={parentRef}
                id="inventory-order-report-table-scroll"
                style={{
                    minHeight: 400,
                    maxHeight: MAX_TABLE_HEIGHT,
                    minWidth: 0,
                    width: '100%',
                    height: Math.max(400, data.length * ROW_HEIGHT),
                    overflow: 'auto',
                    overflowX: 'auto',
                    position: 'relative',
                }}
            >
                <div style={{ minWidth: 'max-content' }}>
                    <div style={{ flexShrink: 0, position: 'sticky', top: 0, zIndex: 11, backgroundColor: '#fff' }}>
                        <TableHeader sortConfig={sortConfig} onSort={onSort} />
                    </div>
                {loading ? (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 48,
                        }}
                    >
                        <CircularProgress />
                    </div>
                ) : data.length === 0 ? (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 48,
                            color: '#666',
                        }}
                    >
                        {emptyMessage}
                    </div>
                ) : (
                    <VirtualTableBody
                        key={`rows-${data.length}`}
                        rows={data}
                        editable={editable}
                        unsavedChanges={unsavedChanges}
                        handlers={handlers}
                        errorIconClassName={errorIconClassName}
                        focusRowId={focusRowId}
                        savingRowId={savingRowId}
                        parentRef={parentRef}
                        virtualizerRef={virtualizerRef}
                    />
                )}
                </div>
            </div>
            <TablePagination
                page={page}
                pageSize={pageSize}
                totalCount={totalCount}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
            />
        </Paper>
    );
};

export default InventoryOrderVirtualTable;
